import React, { useState, useRef } from "react";
import { Box, Popover, IconButton, Tooltip, alpha } from "@mui/material";
import { Calendar, DateObject } from "react-multi-date-picker";
import { CalendarMonth, Close } from "@mui/icons-material";

const latino = {
  months: [
    ["Enero", "Ene"],
    ["Febrero", "Feb"],
    ["Marzo", "Mar"],
    ["Abril", "Abr"],
    ["Mayo", "May"],
    ["Junio", "Jun"],
    ["Julio", "Jul"],
    ["Agosto", "Ago"],
    ["Septiembre", "Sep"],
    ["Octubre", "Oct"],
    ["Noviembre", "Nov"],
    ["Diciembre", "Dic"],
  ],
  weekDays: [["Sabado", "S"], ["Domingo", "D"], ["Lunes", "L"], ["Martes", "M"], ["Miercoles", "X"], ["Jueves", "J"], ["Viernes", "V"]],
  digits: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"],
};

const formatDate = (date) => {
  if (!date) return "";
  const d = date instanceof DateObject ? date.toDate() : new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatDisplay = (dateStr) => {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
};

const DateRangePicker = ({ desde, hasta, onChange, disabled, placeholder }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const tieneFechas = !!desde || !!hasta;
  const label = tieneFechas
    ? hasta
      ? `${formatDisplay(desde)} - ${formatDisplay(hasta)}`
      : formatDisplay(desde)
    : placeholder || "Fechas";

  const calendarValue = [];
  if (desde) calendarValue.push(new DateObject(desde));
  if (hasta) calendarValue.push(new DateObject(hasta));

  const handleChange = (values) => {
    if (!values || values.length === 0) {
      onChange(null, null);
    } else if (values.length === 1) {
      onChange(formatDate(values[0]), null);
    } else {
      onChange(formatDate(values[0]), formatDate(values[1]));
    }
  };

  return (
    <>
      <Box
        ref={anchorRef}
        onClick={() => !disabled && setOpen(true)}
        display="flex"
        alignItems="center"
        gap={0.5}
        sx={{
          px: 0.8,
          py: 0.3,
          borderRadius: 1,
          cursor: disabled ? "default" : "pointer",
          border: "1px solid",
          borderColor: tieneFechas ? (t) => alpha(t.palette.primary.main, 0.5) : "divider",
          bgcolor: tieneFechas ? (t) => alpha(t.palette.primary.main, 0.05) : "transparent",
          transition: "all 0.2s",
          fontSize: "0.75rem",
          color: tieneFechas ? "primary.main" : "text.secondary",
          whiteSpace: "nowrap",
          "&:hover": disabled ? {} : { borderColor: "primary.main" },
        }}
      >
        <CalendarMonth sx={{ fontSize: "0.9rem" }} />
        {label}
        {tieneFechas && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null, null);
            }}
            disabled={disabled}
            sx={{ p: 0.1, ml: 0.3, color: "text.secondary", "&:hover": { color: "error.main" } }}
          >
            <Close sx={{ fontSize: "0.75rem" }} />
          </IconButton>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { mt: 0.5, borderRadius: 2, overflow: "hidden", p: 0.5 } }}
      >
        <Calendar
          range
          numberOfMonths={2}
          locale={latino}
          weekStartDayIndex={1}
          value={calendarValue.length > 0 ? calendarValue : undefined}
          onChange={handleChange}
          onPropsChange={(props) => {
            if (props?.value?.length === 2) {
              setOpen(false);
            }
          }}
          sx={{ fontSize: "0.8rem" }}
        />
      </Popover>
    </>
  );
};

export default DateRangePicker;
